import { type DefaultContext, mutationPromise } from '@egw/clients'
import { createMutationHook, createQueryHook } from '../../hooks'
import {
  type AuthReentryData,
  type AuthReentrySubmitInput,
  type AuthReentrySubmitOutput,
} from '../../models/authn/re-entry'

/**
 * Get Auth Re-entry data with combined fields
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<AuthReentryData>
 * @example const response = await getAuthReentryData()
 */
export const getAuthReentryData = async (context?: DefaultContext): Promise<AuthReentryData> => {
  const response = (await mutationPromise(
    {
      name: 'getAuthReentryData',
      query: `mutation getAuthReentryData($input: ReentryInput!) {
        enterApplication(input: $input) {
          email
          phone {
            phoneNumber
            countryPhoneCode
          }
          nextStep
          error
          path
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { enterApplication: AuthReentryData }
  return response.enterApplication
}

export const useGetAuthReentryData = createQueryHook<undefined, AuthReentryData>(getAuthReentryData)

/**
 * Submit Auth Re-entry data - unified submission handler
 * @param input - Combined input for different submission types
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<AuthReentrySubmitOutput>
 * @example const response = await submitAuthReentryData({ emailVerificationCode: '123456' })
 */
export const submitAuthReentryData = async (
  input: AuthReentrySubmitInput,
  context?: DefaultContext,
): Promise<AuthReentrySubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitAuthReentryData',
      query: `mutation submitAuthReentryData($input: ReentryInput!) {
        enterApplication(input: $input) {
          email
          phone {
            phoneNumber
            countryPhoneCode
          }
          nextStep
          error
          path
        }
      }`,
    },
    {
      input: input || {},
    },
    context,
  )) as { enterApplication: AuthReentrySubmitOutput }
  return response.enterApplication
}

export const useSubmitAuthReentryData = createMutationHook<AuthReentrySubmitInput, AuthReentrySubmitOutput>(
  submitAuthReentryData,
)
